import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  LockOpenIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import CodeMirror from '@uiw/react-codemirror';
import { yaml } from '@codemirror/lang-yaml';
import { json } from '@codemirror/lang-json';

import apiService from '../services/api';
import { ConfigFile, ConfigFileInput } from '../types/api';

// Contraintes miroir de app/schemas/deployment_config_file.py (backend).
// On les duplique cote client pour feedback UX immediat sans round-trip API.
const PATH_WHITELIST_REGEX = /^\/(app|etc\/app|etc\/nginx|config|opt|mnt|data|srv|workspace|usr\/share\/nginx\/html)(\/.+)?$/;
const MAX_FILE_SIZE_BYTES = 100 * 1024; // 100 KB
const MAX_TOTAL_SIZE_BYTES = 900 * 1024; // 900 KB
const MAX_FILES = 10;

// Détecte l'extension du path pour choisir l'extension CodeMirror (highlighting).
// Pas de fallback à un langage : plain text si extension inconnue.
function detectLanguage(path: string) {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  if (ext === 'yml' || ext === 'yaml') return [yaml()];
  if (ext === 'json') return [json()];
  return [];
}

function detectLanguageLabel(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  if (ext === 'yml' || ext === 'yaml') return 'YAML';
  if (ext === 'json') return 'JSON';
  if (ext === 'ini' || ext === 'conf') return 'INI';
  if (ext === 'xml') return 'XML';
  if (ext === 'env') return 'ENV';
  return 'Text';
}

function utf8Size(s: string): number {
  return new Blob([s]).size;
}

interface DraftFile {
  // id local pour React key (pas envoye au backend — le backend gere ses UUIDs)
  localId: string;
  // id backend si le fichier existe deja (undefined si nouveau)
  backendId?: string;
  path: string;
  content: string;
  sensitive: boolean;
  // dirty = modifie par rapport au dernier fetch
  dirty: boolean;
}

interface ConfigFilesEditorProps {
  deploymentId: string;
}

let _idCounter = 0;
const nextLocalId = () => `cf-${++_idCounter}`;

const ConfigFilesEditor: React.FC<ConfigFilesEditorProps> = ({ deploymentId }) => {
  const { t } = (useTranslation as any)('deployments');
  const [files, setFiles] = useState<DraftFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const remote = await apiService.getDeploymentConfigFiles(deploymentId);
      setFiles(
        remote.map((f: ConfigFile) => ({
          localId: nextLocalId(),
          backendId: f.id,
          path: f.path,
          content: f.content,
          sensitive: f.sensitive,
          dirty: false,
        }))
      );
    } catch (err: any) {
      toast.error(t('configFiles.loadError'));
    } finally {
      setLoading(false);
    }
  }, [deploymentId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const totalSize = useMemo(
    () => files.reduce((acc, f) => acc + utf8Size(f.content), 0),
    [files]
  );
  const hasDirty = useMemo(() => files.some((f) => f.dirty), [files]);

  // Validation client-side, message par carte. null = OK.
  const errorsByLocalId = useMemo(() => {
    const errs: Record<string, string | null> = {};
    const seenPaths = new Set<string>();
    for (const f of files) {
      let e: string | null = null;
      if (!f.path.trim()) {
        e = t('configFiles.pathEmpty');
      } else if (!PATH_WHITELIST_REGEX.test(f.path)) {
        e = t('configFiles.pathInvalid');
      } else if (seenPaths.has(f.path)) {
        e = t('configFiles.pathDuplicate');
      } else if (utf8Size(f.content) > MAX_FILE_SIZE_BYTES) {
        e = t('configFiles.sizeExceeded');
      }
      seenPaths.add(f.path);
      errs[f.localId] = e;
    }
    return errs;
  }, [files, t]);

  const hasErrors = useMemo(
    () => Object.values(errorsByLocalId).some((e) => e !== null) || totalSize > MAX_TOTAL_SIZE_BYTES,
    [errorsByLocalId, totalSize]
  );

  const addFile = () => {
    if (files.length >= MAX_FILES) {
      toast.error(t('configFiles.maxFilesReached', { max: MAX_FILES }));
      return;
    }
    setFiles((prev) => [
      ...prev,
      {
        localId: nextLocalId(),
        path: '',
        content: '',
        sensitive: false,
        dirty: true,
      },
    ]);
  };

  const updatePath = (localId: string, path: string) => {
    setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, path, dirty: true } : f)));
  };

  const updateContent = (localId: string, content: string) => {
    setFiles((prev) => prev.map((f) => (f.localId === localId ? { ...f, content, dirty: true } : f)));
  };

  const toggleSensitive = (localId: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.localId === localId ? { ...f, sensitive: !f.sensitive, dirty: true } : f))
    );
  };

  const removeFile = (localId: string) => {
    // Suppression uniquement locale (l'API removera lors du prochain save via
    // le semantic PUT batch qui replace toute la liste).
    setFiles((prev) => prev.filter((f) => f.localId !== localId));
  };

  const save = async () => {
    if (hasErrors) {
      toast.error(t('configFiles.fixErrorsFirst'));
      return;
    }
    setSaving(true);
    try {
      const payload: ConfigFileInput[] = files.map((f) => ({
        path: f.path.trim(),
        content: f.content,
        sensitive: f.sensitive,
      }));
      await apiService.updateDeploymentConfigFiles(deploymentId, payload);
      toast.success(t('configFiles.saveSuccess'));
      await load(); // refetch pour recuperer les backendId + reset dirty
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : t('configFiles.saveError')
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-gray-500">
        {t('configFiles.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header : compteur + bouton ajouter */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            {t('configFiles.title')}
          </h3>
          <span className="text-sm text-gray-500">
            {t('configFiles.sizeInfo', {
              count: files.length,
              max: MAX_FILES,
              sizeKb: (totalSize / 1024).toFixed(1),
              maxKb: (MAX_TOTAL_SIZE_BYTES / 1024).toFixed(0),
            })}
          </span>
        </div>
        <button
          type="button"
          onClick={addFile}
          disabled={files.length >= MAX_FILES}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-md"
        >
          <PlusIcon className="w-4 h-4" />
          {t('configFiles.add')}
        </button>
      </div>

      {/* Empty state */}
      {files.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <DocumentTextIcon className="w-12 h-12 mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">{t('configFiles.empty')}</p>
          <p className="mt-1 text-xs text-gray-500">{t('configFiles.emptyHint')}</p>
        </div>
      )}

      {/* Total size warning */}
      {totalSize > MAX_TOTAL_SIZE_BYTES && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {t('configFiles.totalSizeExceeded', {
            sizeKb: (totalSize / 1024).toFixed(1),
            maxKb: (MAX_TOTAL_SIZE_BYTES / 1024).toFixed(0),
          })}
        </div>
      )}

      {/* Liste de cartes */}
      {files.map((f) => {
        const err = errorsByLocalId[f.localId];
        const size = utf8Size(f.content);
        const lang = detectLanguage(f.path);
        const langLabel = detectLanguageLabel(f.path);
        return (
          <div
            key={f.localId}
            className={`border rounded-lg p-4 ${err ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}
          >
            {/* Path + sensitive + delete */}
            <div className="flex items-center gap-2 mb-2">
              <input
                type="text"
                value={f.path}
                onChange={(e) => updatePath(f.localId, e.target.value)}
                placeholder={t('configFiles.pathPlaceholder') as string}
                className={`flex-1 px-3 py-1.5 text-sm font-mono border rounded-md ${err ? 'border-red-300' : 'border-gray-300'} focus:ring-1 focus:ring-blue-500`}
              />
              <button
                type="button"
                onClick={() => toggleSensitive(f.localId)}
                title={t(f.sensitive ? 'configFiles.markAsPublic' : 'configFiles.markAsSensitive') as string}
                className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium rounded-md ${
                  f.sensitive
                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300'
                }`}
              >
                {f.sensitive ? <LockClosedIcon className="w-4 h-4" /> : <LockOpenIcon className="w-4 h-4" />}
                {t(f.sensitive ? 'configFiles.sensitive' : 'configFiles.public')}
              </button>
              <button
                type="button"
                onClick={() => removeFile(f.localId)}
                title={t('configFiles.delete') as string}
                className="p-1.5 text-red-600 hover:bg-red-100 rounded-md"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>

            {/* Erreur validation */}
            {err && (
              <p className="text-xs text-red-700 mb-2 font-medium">{err}</p>
            )}

            {/* Editor */}
            <CodeMirror
              value={f.content}
              onChange={(v: string) => updateContent(f.localId, v)}
              extensions={lang}
              basicSetup={{ lineNumbers: true, foldGutter: true }}
              minHeight="100px"
              maxHeight="400px"
            />

            {/* Footer : format + size */}
            <div className="mt-1 text-xs text-gray-500 flex justify-between">
              <span>
                {t('configFiles.formatLabel')}: <strong>{langLabel}</strong>
              </span>
              <span className={size > MAX_FILE_SIZE_BYTES ? 'text-red-600 font-semibold' : ''}>
                {size} / {MAX_FILE_SIZE_BYTES / 1024} KB
              </span>
            </div>
          </div>
        );
      })}

      {/* Actions bottom */}
      {(files.length > 0 || hasDirty) && (
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center gap-2 text-xs text-amber-700 mb-3">
            <ExclamationTriangleIcon className="w-4 h-4" />
            {t('configFiles.restartWarning')}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={load}
              disabled={saving || !hasDirty}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {t('configFiles.cancel')}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || hasErrors || !hasDirty}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300"
            >
              {saving ? t('configFiles.saving') : t('configFiles.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigFilesEditor;
